import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
  signal,
} from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import {
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  provideTranslocoScope,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { MzicSvgComponent, SpinnerComponent } from '@mzic/mzic-components';
import { EntityType } from '../../../interfaces/collection.interface';
import { CpfValidator } from '../../../utils/cpf-validator';
import { WaiverCpfFieldComponent } from '../waiver-cpf-field/waiver-cpf-field.component';

export type WaiverUserType = 'rightsholder' | 'representative';

export interface WaiverTypeSelectionResult {
  isRightsholder: boolean;
  cpf?: string;
}

@Component({
  selector: 'lib-waiver-type-selection',
  imports: [
    CommonModule,
    A11yModule,
    TranslocoModule,
    MzicSvgComponent,
    SpinnerComponent,
    WaiverCpfFieldComponent,
  ],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './waiver-type-selection.component.html',
  styleUrl: './waiver-type-selection.component.scss',
})
export class WaiverTypeSelectionComponent {
  private readonly translocoService = inject(TranslocoService);

  @Input() entityType: EntityType | null = null;
  @Input() userName = '';
  @Input() partnerName = '';
  @Input() integrationEnabled = false;
  @Input() integrationEntityName = '';
  @Input() loading = false;
  @Output() confirmed = new EventEmitter<WaiverTypeSelectionResult>();
  @Output() cancel = new EventEmitter<void>();

  selectedType = signal<WaiverUserType | null>(null);
  readonly titleId = 'waiver-type-selection-title';
  readonly descriptionId = 'waiver-type-selection-description';

  readonly cpfForm = new FormGroup({
    cpf: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  get isIndividual(): boolean {
    return this.entityType === 'INDIVIDUAL';
  }

  get isBusiness(): boolean {
    return this.entityType === 'BUSINESS';
  }

  get isRepresentativeSelected(): boolean {
    return this.selectedType() === 'representative';
  }

  get integrationSuffix(): string {
    return this.integrationEnabled ? '_integration' : '';
  }

  get waiverTypeSubtitleIndividualKey(): string {
    return `lang.waiver_type_subtitle_individual${this.integrationSuffix}`;
  }

  get waiverTypeSubtitleBusinessKey(): string {
    return `lang.waiver_type_subtitle_business${this.integrationSuffix}`;
  }

  get cpfInputPrivacyInfoKey(): string {
    return `lang.cpf_input_privacy_info${this.integrationSuffix}`;
  }

  get cpfSectionTitle(): string {
    return this.translocoService.translate(
      'cpf_input_section_title',
      {},
      'mzic-collection-flow',
    );
  }

  get cpfControl(): FormControl<string> {
    return this.cpfForm.get('cpf') as FormControl;
  }

  get isValidCpf(): boolean {
    const value = this.cpfControl.value || '';
    return CpfValidator.validate(value);
  }

  selectType(type: WaiverUserType): void {
    this.selectedType.set(type);
    // Reset CPF when switching types
    if (type === 'rightsholder') {
      this.cpfControl.reset();
    }
  }

  get canContinue(): boolean {
    const type = this.selectedType();
    if (!type || this.loading) return false;

    if (type === 'representative') {
      return this.isValidCpf;
    }

    return true;
  }

  onConfirm(): void {
    const type = this.selectedType();
    if (!type) return;

    if (type === 'representative') {
      if (!this.isValidCpf) {
        this.cpfControl.markAsTouched();
        return;
      }
      const cpf = CpfValidator.unformat(this.cpfControl.value || '');
      this.confirmed.emit({ isRightsholder: false, cpf });
    } else {
      this.confirmed.emit({ isRightsholder: true });
    }
  }

  onCancel(): void {
    this.cancel.emit();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    event.preventDefault();
    this.onCancel();
  }

  onOverlayClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onCancel();
    }
  }
}
